/**
 * API Playground - лётный стенд V2 API для залогиненного партнёра.
 *
 * Дизайн под человека, который ни разу не делал API-запрос:
 *  - Никаких api_secret в браузере. Запросы идут через сервер
 *    (`/web/api/playground/*`) под cookie-сессией пользователя;
 *    бэкенд внутри вызывает ту же бизнес-логику, что и реальный
 *    `/api/v2/*`.
 *  - Если у юзера секрет ещё не выдан - вместо стенда показывается
 *    карточка с просьбой обратиться в поддержку.
 *  - `/pay_order` НЕ доступен через playground - нельзя случайно
 *    списать деньги с песочницы.
 *  - IP-whitelist не проверяется (это cabinet-сессия), но в реальной
 *    интеграции с `/api/v2/*` - обязателен. Об этом отдельный
 *    жёлтый баннер сверху.
 */
import { useEffect, useMemo, useState } from "react";

import { useMe } from "@/api/auth";
import { api, ApiError } from "@/api/client";

interface EndpointSpec {
  /** Path of the cookie-side wrapper (`/web/api/playground/<id>`). */
  id: string;
  /** Mirrored V2 HMAC path the wrapper proxies - for the UI label. */
  hmacPath: string;
  hmacMethod: "GET" | "POST";
  title: string;
  description: string;
  /** Inline form fields, injected into the request body the wrapper
   *  expects. The wrapper accepts JSON; for GET-style endpoints we
   *  pass an empty body.
   *
   *  May include the literal `"{{uuid}}"` token - it will be replaced
   *  with a freshly-generated UUID v4 on every mount + after every
   *  successful run, so the partner doesn't have to type one. */
  bodyTemplate?: string;
  hint?: string;
}

/** Token replaced by a fresh UUID v4 inside body templates. */
const UUID_TOKEN = "{{uuid}}";

function newUuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  // Fallback for ancient browsers - random hex with v4 layout.
  const r = (n: number) =>
    Array.from({ length: n }, () =>
      Math.floor(Math.random() * 16).toString(16),
    ).join("");
  return `${r(8)}-${r(4)}-4${r(3)}-${(8 + Math.floor(Math.random() * 4)).toString(16)}${r(3)}-${r(12)}`;
}

const ENDPOINTS: EndpointSpec[] = [
  {
    id: "stock",
    hmacPath: "/api/v2/stock",
    hmacMethod: "GET",
    title: "Stock - что есть в наличии",
    description:
      "Список категорий и услуг с остатками и итоговой ценой. Каждая категория несёт массив fields - это и есть схема, по которой нужно собирать массив fields в /create_order для услуг этой категории.",
  },
  {
    id: "exchange_rate",
    hmacPath: "/api/v2/exchange_rate",
    hmacMethod: "POST",
    title: "Exchange rate - курсы валют",
    description:
      "Курсы валют, по которым считается итоговая цена в USD для конкретного service_id.",
    bodyTemplate: '{\n  "service_id": 1\n}',
  },
  {
    id: "create_order",
    hmacPath: "/api/v2/create_order",
    hmacMethod: "POST",
    title: "Create order - покупка кода (service_id 449)",
    description:
      "Покупка готового кода (gift card / digital code). Ничего не списывает - это только бронь. В реальной интеграции дальше вызывается /pay_order (тут отключён).",
    bodyTemplate:
      `{\n  "service_id": 449,\n  "custom_id": "${UUID_TOKEN}",\n  "fields": [\n    { "key": "quantity", "value": 1 }\n  ]\n}`,
    hint:
      "custom_id генерируется автоматически. quantity = сколько кодов хочешь. Кнопка ↻ ниже - обновить UUID для следующего теста.",
  },
  {
    id: "create_order",
    hmacPath: "/api/v2/create_order",
    hmacMethod: "POST",
    title: "Create order - пополнение Steam (service_id 1)",
    description:
      "Пополнение Steam-аккаунта. Шаблон полей берётся из ns_field_templates → steam_topup (pricing_mode by_amount).",
    bodyTemplate:
      `{\n  "service_id": 1,\n  "custom_id": "${UUID_TOKEN}",\n  "fields": [\n    { "key": "account", "value": "steam_login" },\n    { "key": "amount", "value": 1.00 }\n  ]\n}`,
    hint:
      "account - Steam-логин или email получателя (плюс символы _ . + -). amount - сумма в USD (0.13 - 500). Никакого region - Steam сам конвертирует.",
  },
  {
    id: "create_order",
    hmacPath: "/api/v2/create_order",
    hmacMethod: "POST",
    title: "Create order - Steam Gift (service_id 394)",
    description:
      "Подарок игры. Шаблон полей из ns_field_templates → steam_gift (pricing_mode external_steam_gift). Перед запуском возьми sub_id из /steam_gift/get_apps.",
    bodyTemplate:
      `{\n  "service_id": 394,\n  "custom_id": "${UUID_TOKEN}",\n  "fields": [\n    { "key": "region", "value": "ru" },\n    { "key": "sub_id", "value": 0 },\n    { "key": "friendLink", "value": "https://s.team/p/abc-defg/12345678" },\n    { "key": "giftName", "value": "Подарок" },\n    { "key": "giftDescription", "value": "" }\n  ]\n}`,
    hint:
      "region - один из ru/kz/ua/cis/cn (нижний регистр). sub_id - числовой id подписки игры (берётся из /steam_gift/get_apps). friendLink - короткий invite https://s.team/p/<hash>/<friend_code>. giftName обязателен, giftDescription нет.",
  },
  {
    id: "check_balance",
    hmacPath: "/api/v2/check_balance",
    hmacMethod: "GET",
    title: "Check balance - твой баланс",
    description:
      "Текущий баланс кабинета (USD, до 4 знаков).",
  },
  {
    id: "order_info",
    hmacPath: "/api/v2/order_info/{custom_id}",
    hmacMethod: "GET",
    title: "Order info - посмотреть заказ",
    description:
      "Текущий статус заказа + (если он завершён) выданные pins или данные доставки. В реальном API это GET с custom_id в URL - здесь для удобства передаётся в теле.",
    bodyTemplate: '{\n  "custom_id": "playground-test-1"\n}',
  },
  {
    id: "steam_gift/get_apps",
    hmacPath: "/api/v2/steam_gift/get_apps",
    hmacMethod: "GET",
    title: "Steam Gift - список игр",
    description:
      "Каталог игр, которые можно купить через Steam Gift.",
  },
  {
    id: "steam/check_user",
    hmacPath: "/api/v2/steam/check_user",
    hmacMethod: "POST",
    title: "Steam - проверка получателя",
    description:
      "Перед созданием Steam-Gift заказа - проверяет что аккаунт получателя существует.",
    bodyTemplate: '{\n  "steam_id": "your_friend_login"\n}',
  },
];

interface RunResult {
  status: number;
  durationMs: number;
  bodyText: string;
}

/* ---------------------------------------------------------------- */

export function Playground() {
  const me = useMe();

  if (me.isLoading) {
    return <div className="text-sm text-slate-500">Loading...</div>;
  }
  if (!me.data) {
    return null; // RequireAuth bounces to /login
  }

  const hasAccess = !!me.data.api_secret_set;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-800">
          API Playground
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-500">
          Стенд для V2 API. Вызывает те же бизнес-функции, что и
          реальный <span className="font-mono">/api/v2/*</span> - но
          под твоей текущей сессией кабинета. Полная документация:{" "}
          <a
            href="/api-docs"
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent underline"
          >
            /api-docs
          </a>
          .
        </p>
      </div>

      {!hasAccess ? (
        <NoAccess />
      ) : (
        <>
          <RealApiNotice />
          <div className="space-y-4">
            {ENDPOINTS.map((ep, i) => (
              <EndpointCard key={`${ep.id}-${i}`} spec={ep} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- */

function NoAccess() {
  return (
    <section className="card border-amber-300 bg-amber-50 p-4 text-amber-900 sm:p-6">
      <h2 className="text-lg font-semibold">
        Доступ к API ещё не выдан
      </h2>
      <p className="mt-2 text-sm">
        Чтобы использовать API и Playground, нужен{" "}
        <span className="font-mono">api_secret</span>. Его выдаёт
        оператор после согласования интеграции.
      </p>
      <p className="mt-2 text-sm">
        <strong>Что делать:</strong> напиши в поддержку - попроси
        выдать api_secret для своего аккаунта. После этого обнови
        страницу, и playground разблокируется автоматически.
      </p>
    </section>
  );
}

function RealApiNotice() {
  return (
    <section className="card border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 sm:p-4">
      <strong>Чем playground отличается от реального API:</strong>
      <ul className="mt-1 list-disc pl-5 space-y-0.5">
        <li>
          Здесь авторизация через cookie-сессию кабинета - HMAC-подпись
          и токен из <code>/get_token</code> не нужны.
        </li>
        <li>
          IP-whitelist <em>не проверяется</em>. В боевой интеграции
          с <code>/api/v2/*</code> запросы с не-белого IP отвергаются
          с 403 - обязательно попроси поддержку добавить IP твоего
          сервера в твой список.
        </li>
        <li>
          <code>/pay_order</code> здесь отключён - реальную оплату
          через playground провести нельзя.
        </li>
      </ul>
    </section>
  );
}

/* ---------------------------------------------------------------- */

function EndpointCard({ spec }: { spec: EndpointSpec }) {
  const hasUuid = !!spec.bodyTemplate?.includes(UUID_TOKEN);
  const initialBody = useMemo(
    () =>
      spec.bodyTemplate
        ? spec.bodyTemplate.split(UUID_TOKEN).join(newUuid())
        : "",
    [spec.bodyTemplate],
  );
  const [bodyText, setBodyText] = useState(initialBody);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<RunResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Reset preview state when spec changes (e.g. via HMR).
  useEffect(() => {
    setBodyText(initialBody);
  }, [initialBody]);

  // Replace whatever sits in `custom_id` with a new UUID v4. Tries
  // structural JSON-edit first (so the user's other tweaks survive);
  // falls back to a regex replace if the body isn't valid JSON yet.
  const regenUuid = () => {
    const fresh = newUuid();
    try {
      const parsed = JSON.parse(bodyText);
      if (parsed && typeof parsed === "object" && "custom_id" in parsed) {
        (parsed as { custom_id: string }).custom_id = fresh;
        setBodyText(JSON.stringify(parsed, null, 2));
        return;
      }
    } catch {
      /* fall through */
    }
    setBodyText((prev: string) =>
      prev.replace(/("custom_id"\s*:\s*")[^"]*(")/, `$1${fresh}$2`),
    );
  };

  const parsedBody = useMemo(() => {
    if (!bodyText.trim()) return undefined;
    try {
      return JSON.parse(bodyText);
    } catch {
      return null; // signals parse error to the caller
    }
  }, [bodyText]);

  const blockers: string[] = [];
  if (spec.bodyTemplate && parsedBody === null) {
    blockers.push("body не валидный JSON");
  }

  const run = async () => {
    if (blockers.length > 0) return;
    setRunning(true);
    setError(null);
    setResult(null);
    const t0 = performance.now();
    try {
      const data = await api.post(`/web/api/playground/${spec.id}`, {
        body: spec.bodyTemplate ? (parsedBody ?? {}) : {},
      });
      const durationMs = Math.round(performance.now() - t0);
      setResult({
        status: 200,
        durationMs,
        bodyText: JSON.stringify(data, null, 2),
      });
      // After a successful create_order, custom_id is now claimed -
      // regenerate so the next click doesn't 409.
      if (hasUuid) regenUuid();
    } catch (err) {
      const e = err as ApiError;
      const durationMs = Math.round(performance.now() - t0);
      // Show the structured error returned by the wrapper / HMAC API.
      const detailStr =
        typeof e.detail === "string"
          ? e.detail
          : JSON.stringify(e.detail, null, 2);
      setResult({
        status: e.status || 0,
        durationMs,
        bodyText: detailStr,
      });
    } finally {
      setRunning(false);
    }
  };

  const methodCls =
    spec.hmacMethod === "POST"
      ? "bg-blue-100 text-blue-700"
      : "bg-emerald-100 text-emerald-700";

  return (
    <section className="card p-4 sm:p-6">
      <header>
        <div className="flex items-center gap-2">
          <span
            className={`rounded px-2 py-0.5 font-mono text-xs font-bold ${methodCls}`}
          >
            {spec.hmacMethod}
          </span>
          <span className="break-all font-mono text-sm text-slate-700">
            {spec.hmacPath}
          </span>
        </div>
        <h3 className="mt-2 text-base font-semibold text-slate-800">
          {spec.title}
        </h3>
        <p className="mt-1 text-sm text-slate-500">{spec.description}</p>
        {spec.hint && (
          <p className="mt-1 text-xs text-amber-700">{spec.hint}</p>
        )}
      </header>

      {spec.bodyTemplate && (
        <div className="mt-3">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Body (JSON)
          </span>
          <textarea
            className="input mt-1 min-h-[8rem] w-full font-mono text-xs"
            value={bodyText}
            onChange={(e) => setBodyText(e.target.value)}
            spellCheck={false}
          />
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="btn btn-primary"
          onClick={run}
          disabled={running || blockers.length > 0}
          title={blockers.length ? blockers.join(" · ") : undefined}
        >
          {running ? "Running..." : "▶ Run"}
        </button>
        {hasUuid && (
          <button
            type="button"
            className="btn btn-ghost text-xs"
            onClick={regenUuid}
            title="Сгенерировать новый custom_id"
          >
            ↻ New UUID
          </button>
        )}
        {blockers.length > 0 && (
          <span className="text-xs text-amber-700">
            ⚠ {blockers.join(" · ")}
          </span>
        )}
      </div>

      {(result || error) && (
        <div className="mt-4 rounded-md border border-surface-border bg-slate-50 p-3">
          {error ? (
            <>
              <div className="text-sm font-semibold text-rose-700">
                Network error
              </div>
              <pre className="mt-1 whitespace-pre-wrap break-all font-mono text-xs text-rose-800">
                {error}
              </pre>
            </>
          ) : (
            result && (
              <>
                <div className="flex items-center gap-2">
                  <span
                    className={`rounded px-2 py-0.5 font-mono text-xs font-bold ${
                      result.status >= 200 && result.status < 300
                        ? "bg-emerald-100 text-emerald-700"
                        : result.status >= 400 && result.status < 500
                          ? "bg-amber-100 text-amber-800"
                          : "bg-rose-100 text-rose-700"
                    }`}
                  >
                    {result.status || "-"}
                  </span>
                  <span className="text-xs text-slate-500">
                    {result.durationMs} ms
                  </span>
                </div>
                <pre className="mt-2 max-h-80 overflow-auto whitespace-pre-wrap break-all rounded bg-white p-2 font-mono text-xs text-slate-800">
                  {result.bodyText || "(empty body)"}
                </pre>
              </>
            )
          )}
        </div>
      )}
    </section>
  );
}
